import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, Briefcase, Landmark, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

// --- Animation Variants ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" as const } },
};

// --- Home Component ---
const Home = () => {
  const navigate = useNavigate();
  return (
    <div className="flex-grow flex flex-col items-center justify-center py-16 px-4 sm:px-6 lg:px-8">
      {/* Hero Section */}
      <motion.section
        className="w-full max-w-5xl mx-auto text-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants} className="flex justify-center mb-6">
          <div className="p-4 bg-slate-100 rounded-full border border-slate-200 text-slate-700 shadow-sm">
            <Landmark className="w-12 h-12" />
          </div>
        </motion.div>

        <motion.div variants={itemVariants}>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-slate-900 mb-4 tracking-tight leading-tight">
            AIIMS Mangalagiri
          </h2>
          <h3 className="text-xl sm:text-2xl font-light text-slate-600 mb-10 tracking-wide uppercase">
            Online Application Portal
          </h3>
          <div className="w-24 h-1 bg-amber-600 mx-auto mb-10"></div>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-16 leading-relaxed">
            Welcome to the official gateway for Admissions and Faculty Recruitment. 
            Please select your respective portal to proceed with your application or administrative tasks.
          </p>
        </motion.div>

        {/* Action Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto"
          variants={containerVariants}
        >
          {/* Student Admission Card */}
          <motion.div
            onClick={() => navigate("/allied")}
            className="group cursor-pointer bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-slate-100 overflow-hidden flex flex-col text-left"
            variants={itemVariants}
          >
            <div className="h-2 bg-slate-800 w-full"></div>
            <div className="p-8 flex flex-col flex-grow">
              <div className="flex items-center justify-between mb-6">
                <div className="p-3 bg-slate-50 text-slate-800 rounded-lg group-hover:bg-slate-800 group-hover:text-white transition-colors">
                  <GraduationCap className="h-8 w-8" />
                </div>
                <ArrowRight className="h-6 w-6 text-slate-300 group-hover:text-slate-800 group-hover:translate-x-1 transition-all" />
              </div>
              <h4 className="text-2xl font-serif text-slate-900 mb-3">Student Admissions</h4>
              <p className="text-slate-600 flex-grow">
                Apply for B.Sc. Allied Health Sciences courses offered by the institute.
              </p>
            </div>
          </motion.div>

          {/* Faculty Recruitment Card */}
          <motion.div
            onClick={() => navigate("/faculty")}
            className="group cursor-pointer bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-slate-100 overflow-hidden flex flex-col text-left"
            variants={itemVariants}
          >
            <div className="h-2 bg-amber-600 w-full"></div>
            <div className="p-8 flex flex-col flex-grow">
              <div className="flex items-center justify-between mb-6">
                <div className="p-3 bg-amber-50 text-amber-700 rounded-lg group-hover:bg-amber-600 group-hover:text-white transition-colors">
                  <Briefcase className="h-8 w-8" />
                </div>
                <ArrowRight className="h-6 w-6 text-slate-300 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
              </div>
              <h4 className="text-2xl font-serif text-slate-900 mb-3">Faculty Recruitment</h4>
              <p className="text-slate-600 flex-grow">
                View open vacancies and apply for faculty, senior resident, and administrative positions.
              </p>
            </div>
          </motion.div>

          {/* Non-Faculty Recruitment Card */}
          <motion.div
            onClick={() => navigate("/non-faculty")}
            className="group cursor-pointer bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-slate-100 overflow-hidden flex flex-col text-left"
            variants={itemVariants}
          >
            <div className="h-2 bg-blue-600 w-full"></div>
            <div className="p-8 flex flex-col flex-grow">
              <div className="flex items-center justify-between mb-6">
                <div className="p-3 bg-blue-50 text-blue-700 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  <Briefcase className="h-8 w-8" />
                </div>
                <ArrowRight className="h-6 w-6 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
              </div>
              <h4 className="text-2xl font-serif text-slate-900 mb-3">Non-Faculty Recruitment</h4>
              <p className="text-slate-600 flex-grow">
                View open vacancies and apply for non-faculty administrative, technical, and support staff positions.
              </p>
            </div>
          </motion.div>

          <motion.div
            onClick={() => navigate("/quarters")}
            className="group cursor-pointer bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-slate-100 overflow-hidden flex flex-col text-left"
            variants={itemVariants}
          >
            <div className="h-2 bg-emerald-600 w-full"></div>
            <div className="p-8 flex flex-col flex-grow">
              <div className="flex items-center justify-between mb-6">
                <div className="p-3 bg-emerald-50 text-emerald-700 rounded-lg group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <Building2 className="h-8 w-8" />
                </div>
                <ArrowRight className="h-6 w-6 text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
              </div>
              <h4 className="text-2xl font-serif text-slate-900 mb-3">Quarters Allotment</h4>
              <p className="text-slate-600 flex-grow">
                Apply for staff accommodation, view vacancy, and manage housing allotment workflows.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </motion.section>
    </div>
  );
};

export default Home;
